import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, X, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { ReviewDetail, Sparkline } from "@/pages/CallReview";

const scoreColor = (s) => (s >= 7.5 ? "#2B6E4F" : s >= 5 ? "#C48A31" : "#8B2E2E");

function RubricEditor() {
  const [criteria, setCriteria] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/call-reviews/rubric").then((r) => { setCriteria(r.data.criteria); setIsDefault(r.data.is_default); });
  useEffect(() => { load(); }, []);

  const update = (i, field, val) => setCriteria(criteria.map((c, idx) => (idx === i ? { ...c, [field]: val } : c)));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/call-reviews/rubric", { criteria });
      setCriteria(data.criteria); setIsDefault(false);
      toast.success("Marking rubric saved");
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to the default sales-call rubric?")) return;
    const { data } = await api.delete("/call-reviews/rubric");
    setCriteria(data.criteria); setIsDefault(true);
    toast.success("Rubric reset to default");
  };

  return (
    <div className="border hairline bg-white p-6" data-testid="rubric-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="font-display text-xl">Marking rubric</div>
          <div className="text-xs text-neutral-500 mt-0.5">
            {isDefault ? "Using the default sales-call rubric — edit and save to customise for this workspace." : "Custom rubric for this workspace."} The AI coach scores every call against these criteria.
          </div>
        </div>
        <div className="flex gap-2">
          {!isDefault && <button className="btn-ghost text-xs flex items-center gap-1" onClick={reset} data-testid="rubric-reset-btn"><RotateCcw size={12} /> Reset to default</button>}
          <button className="btn-primary text-xs flex items-center gap-1" onClick={save} disabled={busy} data-testid="rubric-save-btn"><Save size={12} /> {busy ? "Saving…" : "Save rubric"}</button>
        </div>
      </div>
      <div className="space-y-3">
        {criteria.map((c, i) => (
          <div key={i} className="flex gap-2 items-start" data-testid={`rubric-row-${i}`}>
            <div className="flex-1 grid sm:grid-cols-[220px_1fr] gap-2">
              <input value={c.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Criterion name"
                     className="bg-transparent border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]" data-testid={`rubric-name-${i}`} />
              <input value={c.description || ""} onChange={(e) => update(i, "description", e.target.value)} placeholder="What the AI should look for…"
                     className="bg-transparent border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]" data-testid={`rubric-desc-${i}`} />
            </div>
            <button className="p-2 text-neutral-300 hover:text-[#8B2E2E]" onClick={() => setCriteria(criteria.filter((_, idx) => idx !== i))} data-testid={`rubric-remove-${i}`}><X size={14} /></button>
          </div>
        ))}
      </div>
      {criteria.length < 10 && (
        <button className="btn-ghost text-xs mt-3 flex items-center gap-1" onClick={() => setCriteria([...criteria, { name: "", description: "" }])} data-testid="rubric-add-btn">
          <Plus size={12} /> Add criterion
        </button>
      )}
    </div>
  );
}

function TraineeGroup({ g }) {
  const [open, setOpen] = useState(false);
  const [openReview, setOpenReview] = useState(null);
  const scored = g.reviews.filter((r) => r.status === "complete" && r.overall_score != null);
  return (
    <div className="border hairline bg-white" data-testid={`team-review-group-${g.user_id}`}>
      <button className="w-full flex flex-wrap items-center justify-between gap-4 p-5 text-left" onClick={() => setOpen(!open)}>
        <div>
          <div className="font-medium text-sm">{g.name}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{g.review_count} call{g.review_count !== 1 && "s"} reviewed</div>
        </div>
        <div className="flex items-center gap-6">
          {scored.length >= 2 && <Sparkline points={scored.map((r) => r.overall_score)} />}
          {g.avg_score != null && (
            <div className="text-right">
              <div className="label-eyebrow" style={{ fontSize: "0.5rem" }}>Avg</div>
              <div className="font-display text-2xl" style={{ color: scoreColor(g.avg_score) }}>{g.avg_score}</div>
            </div>
          )}
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </div>
      </button>
      {open && (
        <div className="border-t hairline divide-y divide-neutral-100">
          {[...g.reviews].reverse().map((r) => (
            <div key={r.id}>
              <div className="flex items-center justify-between gap-4 px-5 py-3">
                <div className="text-sm truncate">{r.filename} <span className="font-mono text-xs text-neutral-400 ml-2">{(r.created_at || "").slice(0, 10)}</span></div>
                <div className="flex items-center gap-3">
                  {r.overall_score != null && <span className="font-mono text-sm" style={{ color: scoreColor(r.overall_score) }}>{r.overall_score}/10</span>}
                  {r.status === "complete" && (
                    <button className="btn-ghost text-xs" onClick={() => setOpenReview(openReview === r.id ? null : r.id)}>
                      {openReview === r.id ? "Close" : "View"}
                    </button>
                  )}
                  {r.status !== "complete" && <span className="text-xs text-neutral-400">{r.status}</span>}
                </div>
              </div>
              {openReview === r.id && <ReviewDetail review={r} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const CallReviewsTab = () => {
  const [team, setTeam] = useState({ trainees: [] });
  useEffect(() => { api.get("/call-reviews/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-8" data-testid="call-reviews-tab">
      <RubricEditor />
      <div>
        <div className="label-eyebrow mb-3">Team call reviews</div>
        {team.trainees.length === 0 ? (
          <div className="text-sm text-neutral-500 border hairline bg-white p-8 text-center" data-testid="no-team-reviews">No call reviews yet across the team.</div>
        ) : (
          <div className="space-y-3">{team.trainees.map((g) => <TraineeGroup key={g.user_id} g={g} />)}</div>
        )}
      </div>
    </div>
  );
};
